'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { publishActiveEditorKey } from './active-editor-snapshot'
import { useNotebook } from './use-notebook'

export { useEditorFieldActive } from './use-editor-field-active'

const ActiveEditorContext = createContext(null)

/** @type {(() => void) | null} */
let deactivateActiveEditorRef = null

export function deactivateActiveEditor() {
  deactivateActiveEditorRef?.()
}

export function ActiveEditorProvider({ notebookId, children }) {
  const [activeKey, setActiveKeyState] = useState(null)
  const sessionEpoch = useNotebook((s) => s.sessionEpoch)
  const interactionLocked = useNotebook((s) => s.publicMode || s.locked)

  useLayoutEffect(() => {
    publishActiveEditorKey(activeKey)
  }, [activeKey])

  const setActiveKey = useCallback((updater) => {
    setActiveKeyState((prev) => {
      const next =
        typeof updater === 'function' ? updater(prev) : updater
      if (Object.is(prev, next)) return prev
      publishActiveEditorKey(next)
      return next
    })
  }, [])

  const deactivate = useCallback((key) => {
    setActiveKey((prev) => {
      if (key && prev !== key) return prev
      return null
    })
  }, [setActiveKey])

  const activate = useCallback(
    (key) => {
      if (!key || interactionLocked) return
      setActiveKey(key)
    },
    [interactionLocked, setActiveKey],
  )

  useEffect(() => {
    if (interactionLocked) setActiveKey(null)
  }, [interactionLocked, setActiveKey])

  useEffect(() => {
    deactivateActiveEditorRef = () => setActiveKey(null)
    return () => {
      if (deactivateActiveEditorRef) {
        deactivateActiveEditorRef = null
      }
    }
  }, [setActiveKey])

  useEffect(() => {
    setActiveKey(null)
  }, [notebookId, sessionEpoch, setActiveKey])

  useEffect(() => {
    if (!activeKey) return undefined

    const onPointerDown = (event) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (
        target.closest(
          '[data-nb-tiptap-field], [data-nb-tiptap-static], [data-nb-bubble-toolbar], [data-nb-tab-rename], .nb-badge-picker, .nb-badge-chip',
        )
      ) {
        return
      }

      requestAnimationFrame(() => {
        const active = document.activeElement
        if (
          active instanceof Element &&
          active.closest(
            '[data-nb-tiptap-field], [data-nb-tiptap-static], [data-nb-bubble-toolbar]',
          )
        ) {
          return
        }
        void useNotebook.getState().flushPendingSaves?.()
        setActiveKey(null)
      })
    }

    document.addEventListener('mousedown', onPointerDown, true)
    document.addEventListener('touchstart', onPointerDown, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true)
      document.removeEventListener('touchstart', onPointerDown, true)
    }
  }, [activeKey, setActiveKey])

  const value = useMemo(
    () => ({
      activeKey,
      activate,
      deactivate,
      isActive: (key) => Boolean(key) && activeKey === key,
    }),
    [activeKey, activate, deactivate],
  )

  return (
    <ActiveEditorContext.Provider value={value}>
      {children}
    </ActiveEditorContext.Provider>
  )
}

export function useActiveEditor() {
  const ctx = useContext(ActiveEditorContext)
  if (!ctx) {
    throw new Error('useActiveEditor must be used within ActiveEditorProvider')
  }
  return ctx
}
